import {LocalDate} from "./localDate";
import {DatePeriod} from "./datePeriod";
import type {Maybe} from "../../../bootstrap/common/types";

export const YEAR_REGEX = /^\d+$/;
export const RELATIVE_DATE_REGEX = /(^[-|+]?\d+)([ymwd])/;
export const GERMAN_DATE_REGEX = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;


export function dateExpressionFrom(input: string): DateExpression {
    return AbsoluteYearExpression.parse(input)
        ?? GermanDateExpression.parse(input)
        ?? RelativeDateExpression.parse(input)
        ?? HTML5DateExpression.parse(input)
        ?? InvalidDateExpression.of(input);
}

export interface DateExpression {
    toLocalDate: () => LocalDate;
}

export class AbsoluteYearExpression implements DateExpression {

    public constructor(private year: number) {
    }

    public static parse(input: string): Maybe<AbsoluteYearExpression> {
        if (YEAR_REGEX.test(input)) {
            return new AbsoluteYearExpression(Number.parseInt(input));
        } else {
            return undefined;
        }
    }

    public toLocalDate(): LocalDate {
        return LocalDate.fromYMD(this.year, 1, 1);
    }

}

export class GermanDateExpression implements DateExpression {

    public constructor(private year: number, private month: number, private day: number) {
    }

    public static parse(input: string): Maybe<GermanDateExpression> {
        const matches = GERMAN_DATE_REGEX.exec(input) ?? [];
        if (matches === null || matches.length < 4) {
            return undefined;
        }
        return new GermanDateExpression(matches[3].toInt(), matches[2].toInt(), matches[1].toInt());
    }

    public toLocalDate(): LocalDate {
        return LocalDate.fromYMD(this.year, this.month, this.day);
    }

}

export class RelativeDateExpression implements DateExpression {

    public constructor(private number: number, private unit: string) {
    }

    public static parse(input: string): Maybe<RelativeDateExpression> {
        const parsed = RELATIVE_DATE_REGEX.exec(input);
        if (parsed) {
            return new RelativeDateExpression(Number.parseInt(parsed[1]), parsed[2]);
        } else {
            return undefined;
        }
    }

    public toLocalDate(): LocalDate {
        return LocalDate.today().add(this.toDatePeriod());
    }

    public toDatePeriod(): DatePeriod {
        return new DatePeriod(this.number, this.unit);
    }
}

export class HTML5DateExpression implements DateExpression {

    public constructor(private year: number, private month: number, private day: number) {
    }

    public static parse(input: string): Maybe<HTML5DateExpression> {
        const trydate = new Date(input);
        const year = trydate.getFullYear();
        const month = trydate.getMonth();
        const day = trydate.getDate();
        if (Number.isNaN(year + month + day)) {
            return undefined;
        }
        return new HTML5DateExpression(year, month + 1, day);
    }

    public toLocalDate(): LocalDate {
        return LocalDate.fromYMD(this.year, this.month, this.day);
    }
}

export class InvalidDateExpression implements DateExpression {

    public constructor(private errorToken: string) {
    }

    public static of(input: string): InvalidDateExpression {
        return new InvalidDateExpression(input);
    }

    public toLocalDate(): LocalDate {
        return LocalDate.invalid();
    }

}
